import { Box, Paper, Typography } from "@mui/material";
import {
	CellKeyDownEvent,
	GridApi,
	IStatusPanelParams,
	RowClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { useSearchParams } from "react-router-dom";
import { ErrorModal } from "../../../../components/modals/error-modal";
import { CenteredLazyLoading } from "../../../../components/shared/CenteredLazyLoading";
import { ExcelButton } from "../../../../components/shared/buttons/ExcelButton";
import { useLogbookSettings } from "../../../../context/LogbookSettingsContext";
import { useUserContext } from "../../../../context/userContext";
import { SearchShiftsParameters } from "../../../../controls";
import { getMomentTz } from "../../../../services";
import { Entry } from "../../entry/models/Entry";
import { useGridDefinitions } from "../../useGridDefinitions";
import { useEntriesReport } from "../hooks/useEntriesReport";
import { useTableData } from "../hooks/useTableData";
import { EntryViewer } from "./entry-viewer/EntryViewer";
import { Filters } from "./filters/filters";

const moment = getMomentTz();

// Default layout matching original Grid md:5 / md:7 ratio (41.67% / 58.33%)
const DEFAULT_LAYOUT = { left: 42, right: 58 };

export const Logbook = () => {
	const { report } = useLogbookSettings();
	const {
		state: { defaultAreaId },
	} = useUserContext();

	const withAssetFilter = report.withAssetFilter;
	const filterAssets = report.filterAssets;
	const showAttachments = report.showAttachments;
	const showAsset = report.showAsset;
	const showShiftCrew = report.showShiftCrew;
	const exportToExcel = report.exportToExcel;

	const [searchParams, setSearchParams] = useSearchParams();

	const entryId = searchParams.get("entryId");

	const gridApiRef = useRef<GridApi | null>(null);

	const [error, setError] = useState("");
	const [show, setShow] = useState<"edit" | "view" | "">("");
	const [assetId, setAssetId] = useState<number | null>(
		typeof defaultAreaId === "number" ? defaultAreaId : null,
	);
	const initializedAssetRef = useRef(false);
	const [shiftPeriod, setShiftPeriod] = useState<SearchShiftsParameters>(() => {
		const startShiftIdParam = searchParams.get("startShiftId");
		const endShiftIdParam = searchParams.get("endShiftId");

		return {
			StartShiftId: startShiftIdParam ? Number(startShiftIdParam) : null,
			EndShiftId: endShiftIdParam ? Number(endShiftIdParam) : null,
		};
	});
	const [quickFilter, setQuickFilter] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [selectedRowData, setSelectedRowData] = useState<Entry | null>(null);

	const isAllPeriod = searchParams.get("period") === "All";

	const updateUrl = ({
		startShiftId,
		endShiftId,
		period,
		entryId,
	}: {
		startShiftId?: number | null;
		endShiftId?: number | null;
		period?: string | null;
		entryId?: string | null;
	}) => {
		const newParams = new URLSearchParams(searchParams);

		if (startShiftId) {
			newParams.set("startShiftId", startShiftId.toString());
		} else if (startShiftId === null) {
			newParams.delete("startShiftId");
		}

		if (endShiftId) {
			newParams.set("endShiftId", endShiftId.toString());
		} else if (endShiftId === null) {
			newParams.delete("endShiftId");
		}

		if (period) {
			newParams.set("period", period);
		} else if (period === null) {
			newParams.delete("period");
		}

		if (entryId) {
			newParams.set("entryId", entryId);
		} else if (entryId === null) {
			newParams.delete("entryId");
		}

		setSearchParams(newParams);
	};

	const {
		data: rows,
		isLoading,
		error: e,
		isError,
		isFetching,
	} = useEntriesReport({
		startShiftId: shiftPeriod.StartShiftId,
		endShiftId: shiftPeriod.EndShiftId,
		assetId: assetId,
		searchText: isAllPeriod ? debouncedSearch : null,
		isAllPeriod,
	});

	const { columnDefs } = useTableData({
		showAttachments,
		showAsset,
		showShiftCrew,
	});
	const { defaultColDef } = useGridDefinitions({});

	// Resizable panels layout persistence
	const { defaultLayout: savedLayout, onLayoutChange } = useDefaultLayout({
		id: "logbook-report-v2",
		storage: localStorage,
	});
	// Use saved layout or default to avoid flash on initial render
	const resolvedLayout = savedLayout ?? DEFAULT_LAYOUT;

	const onQuickFilterChange = (text: string) => {
		setQuickFilter(text);
	};

	const rowClicked = (event: RowClickedEvent) => {
		setSelectedRowData(event?.data);
		updateUrl({ entryId: event.data?.EntryId?.toString() });
		setShow("view");
	};

	const onCellKeyDown = useCallback(
		(event: CellKeyDownEvent) => {
			const { api, node, event: keyEvent } = event;
			if (!keyEvent) return;
			const key = (keyEvent as KeyboardEvent).key;

			if (key === "ArrowUp" || key === "ArrowDown") {
				keyEvent.preventDefault();
				const currentIndex = node.rowIndex !== null ? node.rowIndex : -1;
				const nextRowIndex =
					key === "ArrowUp" ? currentIndex - 1 : currentIndex + 1;
				const nextNode = api.getDisplayedRowAtIndex(nextRowIndex);

				if (nextNode) {
					nextNode.setSelected(true);
					api.ensureIndexVisible(nextRowIndex);
					setSelectedRowData(nextNode.data);
					updateUrl({ entryId: nextNode.data?.EntryId?.toString() });
				}
			}
		},
		[setSelectedRowData],
	);

	const handleExportToExcel = () => {
		if (!gridApiRef.current) return;

		const params = {
			fileName: "Logbook_Report_Export.xlsx",
			columnKeys: [
				"AssetName",
				"CreateTimestamp",
				"Shift",
				"Crew",
				"Title",
				"CreatedBy",
				"Author",
				"Content",
			],
			processCellCallback: (params: any) => {
				if (params.column.colId === "CreateTimestamp") {
					return moment(params.value).format("MM/DD/yy - HH:mm");
				}
				return params.value;
			},
		};
		gridApiRef.current.exportDataAsExcel(params);
	};

	useEffect(() => {
		const handle = setTimeout(() => setDebouncedSearch(quickFilter), 400);
		return () => clearTimeout(handle);
	}, [quickFilter]);

	useEffect(() => {
		if (!isLoading && entryId && gridApiRef.current) {
			const rowNode = gridApiRef.current?.getRowNode(entryId);

			if (rowNode) {
				gridApiRef.current?.ensureNodeVisible(rowNode);

				rowNode.setSelected(true);
				setSelectedRowData(rowNode.data);
				setShow("view");
			}
		} else if (!entryId) {
			setShow("");
			setSelectedRowData(null);
			gridApiRef.current?.deselectAll();
		}
	}, [gridApiRef.current, isLoading, entryId]);

	useEffect(() => {
		if (isError) {
			setError(e.message);
		}
	}, [isError, e]);

	// Sync assetId with defaultAreaId when it becomes available (only once on initial load)
	useEffect(() => {
		if (!initializedAssetRef.current && typeof defaultAreaId === "number") {
			setAssetId(defaultAreaId);
			initializedAssetRef.current = true;
		}
	}, [defaultAreaId]);

	useEffect(() => {
		const startShiftIdParam = searchParams.get("startShiftId");
		const endShiftIdParam = searchParams.get("endShiftId");
		const entryIdParam = searchParams.get("entryId");

		if (startShiftIdParam || endShiftIdParam) {
			setShiftPeriod({
				StartShiftId: startShiftIdParam ? Number(startShiftIdParam) : null,
				EndShiftId: endShiftIdParam ? Number(endShiftIdParam) : null,
			});
		}

		// Handle entryId from URL on initial load
		if (entryIdParam && gridApiRef.current) {
			const rowNode = gridApiRef.current?.getRowNode(entryIdParam);
			if (rowNode) {
				rowNode.setSelected(true);
				setSelectedRowData(rowNode.data);
				setShow("view");
			}
		}
	}, []);

	useEffect(() => {
		if (shiftPeriod.StartShiftId !== null || shiftPeriod.EndShiftId !== null) {
			setShow("");
			setSelectedRowData(null);
		}
	}, [shiftPeriod.StartShiftId, shiftPeriod.EndShiftId]);

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Box sx={{ mb: 2 }}>
				<Typography
					variant="h5"
					style={{
						fontWeight: 600,
						userSelect: "none",
					}}
				>
					LOGBOOK REPORT
				</Typography>
			</Box>
			<Group
				id="logbook-report-group"
				orientation="horizontal"
				defaultLayout={resolvedLayout}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel
					id="left"
					defaultSize={42}
					minSize={25}
					style={{ paddingBottom: 5 }}
				>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 1,
							height: "100%",
							pr: 1,
						}}
					>
						<Filters
							withAssetFilter={withAssetFilter}
							filterAssets={filterAssets}
							quickFilter={quickFilter}
							onQuickFilterChange={onQuickFilterChange}
							assetId={assetId}
							setAssetId={(value) => setAssetId(value)}
							shiftPeriod={shiftPeriod}
							onChangeShiftPeriod={(value, period) => {
								setShiftPeriod(value);
								updateUrl({
									startShiftId: value.StartShiftId,
									endShiftId: value.EndShiftId,
									period,
									entryId: searchParams.get("entryId"),
								});
							}}
							actualPeriod={searchParams.get("period")}
						/>
						<Paper
							elevation={1}
							style={{
								flex: 1,
								width: "100%",
							}}
						>
							<AgGridReact<Entry>
								loading={isFetching}
								rowData={rows}
								quickFilterText={isAllPeriod ? "" : quickFilter}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={38}
								headerHeight={42}
								loadingOverlayComponent={CenteredLazyLoading}
								animateRows
								onRowClicked={rowClicked}
								onCellKeyDown={onCellKeyDown}
								onGridReady={(params) => (gridApiRef.current = params.api)}
								getRowId={(params) => `${params.data.EntryId}`}
								gridOptions={{
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: true,
									},
									statusBar: {
										statusPanels: [
											exportToExcel
												? {
														statusPanel: (params: IStatusPanelParams) => (
															<div
																style={{
																	display: "flex",
																	alignItems: "center",
																	paddingTop: 1,
																	paddingBottom: 1,
																}}
															>
																<ExcelButton
																	handleExportToExcel={handleExportToExcel}
																/>
															</div>
														),
														align: "right",
													}
												: {},
										],
									},
								}}
							/>
						</Paper>
					</Box>
				</Panel>
				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 8,
							height: 48,
							borderRadius: 1,
							backgroundColor: "#e0e0e0",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							gap: "3px",
							transition: "background-color 0.2s",
							"&:hover": {
								backgroundColor: "primary.main",
								"& .grip-dot": {
									backgroundColor: "#fff",
								},
							},
						}}
					>
						{[...Array(5)].map((_, i) => (
							<Box
								key={i}
								className="grip-dot"
								sx={{
									width: 4,
									height: 4,
									borderRadius: "50%",
									backgroundColor: "#9e9e9e",
									transition: "background-color 0.2s",
								}}
							/>
						))}
					</Box>
				</Separator>
				<Panel
					id="right"
					defaultSize={58}
					minSize={30}
					style={{ paddingBottom: 5 }}
				>
					<Box sx={{ pl: 1, height: "100%" }}>
						<EntryViewer
							entry={selectedRowData}
							show={show}
							onChangeShow={() => setShow(show === "view" ? "edit" : "view")}
							onHide={() => {
								setShow("");
								setSelectedRowData(null);
								gridApiRef.current?.deselectAll();
								updateUrl({ entryId: null });
							}}
							filterAssets={filterAssets}
							showAsset={showAsset}
							showAttachments={showAttachments}
						/>
					</Box>
				</Panel>
			</Group>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
